import { ArrowDown01Icon, CancelCircleIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import React, { useMemo, useState } from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Menu } from 'react-native-paper';
import { Colors } from '../constants/Colors';
import { useDynamicStyles } from '../hooks/useDynamicStyles';
import { isIos, type WithResponsive } from '../utils/responsivity';
import { Button } from './Button';
import { Loader } from './Loader';
import { TextReg } from './StyledText';
import { TextInput } from './TextInput';

type Props<T extends string | number> = {
  placeholder: string;
  options: Array<{ label: string; value: T }>;
  value?: T;
  onChange: (value: T) => void;
  onClear?: () => void;
  buttonStyle?: StyleProp<ViewStyle>;
  menuStyle?: StyleProp<ViewStyle>;
  notClearable?: boolean;
  variant?: 'slim' | 'normal';
  showLoadingState?: boolean;
  searchPlaceholder?: string;
  minSearchLength?: number;
};

export function SearchableDropdown<T extends string | number>({
  placeholder,
  options,
  value,
  onChange,
  onClear,
  buttonStyle,
  notClearable,
  variant = 'normal',
  menuStyle,
  showLoadingState,
  searchPlaceholder = 'Search...',
  minSearchLength = 3,
}: Props<T>) {
  const [visible, setVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const styles = useDynamicStyles(createStyles<T>, { variant });

  const openMenu = () => {
    setVisible(true);
    setSearchText('');
  };

  const closeMenu = () => {
    setVisible(false);
    setSearchText('');
  };

  const selectItem = (value: T) => {
    onChange(value);
    closeMenu();
  };

  const clearValue = () => {
    onClear?.();
  };

  // Filter options based on search text with minimum length requirement
  const filteredOptions = useMemo(() => {
    const trimmedSearch = searchText.trim();

    // Don't show any options if search text is shorter than minimum length
    if (trimmedSearch.length < minSearchLength) {
      return [];
    }

    const searchLower = trimmedSearch.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchLower),
    );
  }, [options, searchText, minSearchLength]);

  const handlePress = () => {
    if (options.length) {
      openMenu();
    }
  };

  const handleIconPress = () => {
    clearValue();
  };

  const canClearSelection = value !== undefined && !notClearable;
  const selectedOption = options.find((option) => option.value === value);

  return (
    <View>
      <Menu
        visible={visible}
        onDismiss={closeMenu}
        contentStyle={[
          styles.menu,
          menuStyle,
          isIos && { marginTop: variant === 'slim' ? 44 : 56 },
        ]}
        anchor={
          <Button style={[styles.content, buttonStyle]} onPress={handlePress}>
            <View style={styles.buttonContent}>
              {selectedOption ? (
                <TextReg numberOfLines={1} style={styles.text}>
                  {selectedOption.label}
                </TextReg>
              ) : (
                <TextReg numberOfLines={1} style={styles.placeholder}>
                  {placeholder}
                </TextReg>
              )}
              <View style={styles.iconContainer}>
                {options.length ? (
                  canClearSelection ? (
                    <TouchableOpacity
                      onPress={handleIconPress}
                      style={styles.clearButton}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    >
                      <HugeiconsIcon
                        icon={CancelCircleIcon}
                        color={Colors.text}
                      />
                    </TouchableOpacity>
                  ) : (
                    <HugeiconsIcon icon={ArrowDown01Icon} color={Colors.text} />
                  )
                ) : (
                  showLoadingState && (
                    <Loader style={{ alignItems: 'flex-end' }} />
                  )
                )}
              </View>
            </View>
          </Button>
        }
      >
        <View style={styles.searchContainer}>
          <TextInput
            label={searchPlaceholder}
            placeholder={searchPlaceholder}
            value={searchText}
            onChange={setSearchText}
            style={styles.searchInput}
          />
        </View>
        {filteredOptions.length === 0 ? (
          <View style={styles.noResultsContainer}>
            <TextReg style={styles.noResultsText}>
              {searchText.trim().length > 0 &&
              searchText.trim().length < minSearchLength
                ? `Type at least ${minSearchLength} characters to search`
                : searchText.trim().length >= minSearchLength
                  ? 'No results found'
                  : 'Start typing to search...'}
            </TextReg>
          </View>
        ) : (
          filteredOptions.map((option) => (
            <Menu.Item
              key={option.value}
              onPress={() => selectItem(option.value)}
              title={option.label}
              titleStyle={styles.menuItemTitle}
            />
          ))
        )}
      </Menu>
    </View>
  );
}

function createStyles<T extends string | number>({
  variant,
}: WithResponsive<Props<T>>) {
  return StyleSheet.create({
    buttonLabel: {
      paddingRight: 16,
    },
    absoluteContent: {
      position: 'absolute',
      backgroundColor: 'transparent',
      pointerEvents: 'none',
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingLeft: 16,
      paddingRight: 16,
      height: variant === 'slim' ? 44 : 56,
      width: '100%',
      gap: 10,
    },
    content: {
      height: variant === 'slim' ? 44 : 56,
    },
    buttonContent: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      paddingHorizontal: 16,
    },
    text: {
      fontSize: 16,
      flex: 1,
    },
    placeholder: {
      fontSize: 16,
      color: Colors.placeholderText,
      flex: 1,
    },
    iconContainer: {
      marginLeft: 8,
    },
    clearButton: {
      padding: 4,
      margin: -4,
    },
    menu: {
      backgroundColor: Colors.purple0,
      borderRadius: 8,
      maxHeight: 300,
    },
    searchContainer: {
      padding: 8,
      borderBottomWidth: 1,
      borderBottomColor: Colors.opacity20,
    },
    searchInput: {
      backgroundColor: Colors.opacity05,
      borderRadius: 8,
      fontSize: 14,
    },
    noResultsContainer: {
      padding: 16,
      alignItems: 'center',
    },
    noResultsText: {
      color: Colors.placeholderText,
      fontSize: 14,
    },
    menuItemTitle: {
      fontSize: 16,
      color: Colors.text,
    },
  });
}
